/**
 * Preset B — "icon-rail": narrow vertical rail of icons on the far left,
 * with a secondary panel that slides in for the focused module.
 */
import { useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { Brand } from "./Brand";
import { Topbar } from "./Topbar";
import { adminNav } from "./nav";
import { useFilteredAdminNav } from "@/hooks/useEnabledNav";
import { cn } from "@/lib/utils";

export function AdminLayoutB() {
  const location = useLocation();
  const filteredNav = useFilteredAdminNav();

  // Identify the active top-level item from the URL so the panel auto-loads.
  const activeTop =
    filteredNav.find(
      (n) =>
        (n.to && location.pathname.startsWith(n.to)) ||
        n.children?.some((c) => c.to && location.pathname.startsWith(c.to)),
    ) ?? filteredNav[0];

  const [hovered, setHovered] = useState<string | null>(null);
  const [navOpen, setNavOpen] = useState(false);
  const focused =
    filteredNav.find((n) => n.label === hovered) ?? activeTop;

  return (
    <div className="min-h-screen bg-bg flex">
      {navOpen && (
        <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={() => setNavOpen(false)} aria-hidden />
      )}
      <div className={`fixed inset-y-0 left-0 z-50 flex transition-transform duration-200 lg:static lg:translate-x-0 lg:z-auto ${navOpen ? "translate-x-0" : "-translate-x-full"}`}>
      {/* Rail */}
      <aside
        className="sticky top-0 h-screen w-[68px] shrink-0 border-r border-border bg-bg-subtle flex flex-col items-center py-4 gap-1 lg:bg-bg-subtle/50"
        onMouseLeave={() => setHovered(null)}
      >
        <div className="mb-3"><Brand collapsed /></div>
        <div className="flex-1 w-full overflow-y-auto px-2 space-y-1" onClick={() => setNavOpen(false)}>
          {filteredNav.map((item) => {
            const Icon = item.icon;
            const isActive = item === activeTop;
            const target = item.to ?? item.children?.[0]?.to ?? "#";
            return (
              <NavLink
                key={item.label}
                to={target}
                onMouseEnter={() => setHovered(item.label)}
                title={item.label}
                className={cn(
                  "relative grid place-items-center h-10 w-full rounded-lg transition-colors",
                  isActive
                    ? "bg-primary/15 text-primary"
                    : "text-fg-muted hover:bg-bg-elevated/60 hover:text-fg",
                )}
              >
                {Icon && <Icon className="h-[18px] w-[18px]" />}
                {isActive && (
                  <span className="absolute left-0 top-2 bottom-2 w-0.5 rounded-r bg-primary" />
                )}
              </NavLink>
            );
          })}
        </div>
      </aside>

      {/* Secondary panel */}
      <aside className="sticky top-0 h-screen w-60 shrink-0 border-r border-border bg-bg-subtle flex flex-col lg:bg-bg-subtle/30">
        <div className="h-16 px-5 flex items-center border-b border-border">
          <span className="text-sm font-semibold text-fg flex items-center gap-2">
            {focused.icon && <focused.icon className="h-4 w-4 text-primary" />}
            {focused.label}
          </span>
        </div>
        <nav className="flex-1 overflow-y-auto p-3 space-y-0.5" onClick={() => setNavOpen(false)}>
          {focused.children?.length ? (
            focused.children.map((c) =>
              c.to ? (
                <NavLink
                  key={c.label}
                  to={c.to}
                  className={({ isActive }) =>
                    cn(
                      "block rounded-md px-3 py-2 text-[13px] transition-colors",
                      isActive
                        ? "bg-primary/15 text-primary font-medium"
                        : "text-fg-subtle hover:text-fg hover:bg-bg-elevated/60",
                    )
                  }
                >
                  <span className="inline-flex items-center gap-2">
                    <span className="h-1 w-1 rounded-full bg-current opacity-60" />
                    {c.label}
                  </span>
                </NavLink>
              ) : null,
            )
          ) : focused.to ? (
            <NavLink
              to={focused.to}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-2 rounded-md px-3 py-2 text-[13px] transition-colors",
                  isActive
                    ? "bg-primary/15 text-primary font-medium"
                    : "text-fg-subtle hover:text-fg hover:bg-bg-elevated/60",
                )
              }
            >
              Open <ChevronRight className="h-3 w-3" />
            </NavLink>
          ) : null}
        </nav>
        <div className="p-3 border-t border-border">
          <p className="text-[11px] text-fg-subtle">Hover icons to browse modules.</p>
        </div>
      </aside>
      </div>

      <div className="flex-1 flex flex-col min-w-0">
        <Topbar onMenuToggle={() => setNavOpen(true)} />
        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-[1600px] w-full mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
